import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import _ from "lodash";
import cx from "classnames";

import { AssetCard, PlaceholderAssetCard } from "../card";

import { AssetLayoutProps } from "../../../types";
import Button from "../../../../../components/button";

import { CollectionDescription } from "src/pages/collection/components/description";

import { GridStack } from "gridstack";
import { CustomerLogo } from "src/pages/collection/components/customerLogo";
import { calculateDimensionsForCard, getAssetHasDimensions } from "./helper";

import styles from "./layout.module.scss";
import sectionStyles from "../sections/sections.module.scss";
import useMeasure from "react-use-measure";
import { CollectionContext } from "src/pages/collection/components/context";
import { ColorPicker } from "src/components/color";
import { USER_DEFINED } from "src/constants/collections";
import { ContactCard } from "../contactCard";

export interface BentoContextType {
  gridStack: React.RefObject<GridStack | null> | null;
}

export const BentoContext = React.createContext<BentoContextType>({
  gridStack: null,
});

const BentoLayout: React.FC<AssetLayoutProps> = ({
  loading,
  collection,
  section,
  assets,
  visible,
  menuState,
  onMenuChange,
  onRemove,
  onMove,
  onMoveToSection,
  onClick,
}) => {
  const collectionContext = useContext(CollectionContext);

  const gridRef = useRef(null);
  const gridStackRef = useRef<GridStack | null>(null);
  const descriptionRef = useRef(null);

  const editing = menuState.primaryAction !== "view";
  const showPlaceholder = loading && _.size(assets) === 0;
  const empty = !loading && _.size(assets) === 0;

  const [descriptionColor, setDescriptionColor] = useState<any>({
    r: 0,
    g: 0,
    b: 0,
    a: 0.1,
  });

  const isDescriptionWidgetPlaced = true;

  const [bentoContext, setBentoContext] = React.useState<BentoContextType>({
    gridStack: gridStackRef,
  });

  const assetsWithoutDimensions = useMemo(() => {
    return _.filter(assets, (asset) => !getAssetHasDimensions(asset));
  }, [assets]);

  const [dragging, setDragging] = React.useState(false);
  const draggingAssetRef = useRef<HTMLElement | null>(null);
  const sectionOverRef = useRef(-1);

  const [descriptionElementRef, descriptionBounds] = useMeasure();

  const canEdit = useMemo(() => {
    return collection?.type == USER_DEFINED && collection?.canEdit;
  }, [collection]);

  const handleInitialised = useCallback((node) => {
    gridRef.current = node;
    initializeGrid();
  }, []);

  useEffect(() => {
    setBentoContext({
      gridStack: gridStackRef,
    });
  }, [gridStackRef.current]);

  useEffect(() => {
    if (editing) {
      document.addEventListener("mousemove", handleMouseMove);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
    };
  }, [dragging]);

  useEffect(() => {
    gridStackRef.current?.on("dragstop", handleDragStop);
    gridStackRef.current?.on("dropped", handleDragStop);

    return () => {
      gridStackRef.current?.off("dragstop");
      gridStackRef.current?.off("dropped");
    };
  }, [assets]);

  useEffect(() => {
    if (collection.banner && collection.banner.descriptionOverlay) {
      setDescriptionColor(collection.banner.descriptionOverlay);
    }
  }, [collection.banner]);

  useEffect(() => {
    if (_.size(assetsWithoutDimensions) > 0) {
      const dimensions = assetsWithoutDimensions.map((asset) => {
        const assetIndex = _.findIndex(assets, { id: asset.id });
        const dimension = calculateDimensionsForCard(
          gridStackRef.current,
          assetIndex + 1,
          false,
          _.get(asset, "type") === "CONTACT_CARD",
        );
        return {
          id: asset.id,
          metadata: {
            ...asset.metadata,
            ...dimension,
          },
        };
      });

      collectionContext.updateAssetMetadata(section, dimensions);
    }
  }, [assetsWithoutDimensions]);

  useEffect(() => {
    // making grid static when not in edit mode
    if (gridStackRef.current) {
      gridStackRef.current.setStatic(!editing);
    }
  }, [editing]);

  useEffect(() => {
    if (menuState.secondaryAction === "reset-layout") {
      resetLayout();
      onMenuChange({
        ...menuState,
        secondaryAction: null,
      });
    }
  }, [menuState.secondaryAction]);

  const initializeGrid = () => {
    if (gridRef.current) {
      if (gridStackRef.current) {
        try {
          gridStackRef.current?.off("change");
          gridStackRef.current?.off("dragstart");
          gridStackRef.current?.off("dragstop");
          gridStackRef.current?.off("dropped");
          gridStackRef.current?.destroy(false);
          gridStackRef.current = null;
        } catch (e) {
          console.log(e);
        }
      }

      gridStackRef.current = GridStack.init(
        {
          auto: false, // Disable auto-initialization
          column: 6, // Number of columns
          float: false, // Enable floating widgets,
          animate: false, // Enable animation
          columnOpts: {
            layout: "compact",
          },
        },
        gridRef.current,
      );

      if (descriptionRef.current) {
        gridStackRef.current.makeWidget(descriptionRef.current);
      }

      gridStackRef.current!.on("change", handleGridChange);
      gridStackRef.current!.on("dragstart", handleDragStart);
      gridStackRef.current!.on("dragstop", handleDragStop);
      gridStackRef.current!.on("dropped", handleDragStop);
    }
  };

  const handleGridChange = (event, items) => {
    if (_.size(items) === 0) return;

    const changes = items.map((item) => {
      const id = item.id;
      const elementClassList = item.el.classList ?? [];
      const isContactCard = elementClassList.contains("contact-card");

      const modifiedAssetMetadata = {
        ...item.metadata,
        id,
        metadata: {
          xaxis: item.x,
          yaxis: item.y,
          width: item.w,
          height: isContactCard ? 1 : item.h,
        },
      };

      return modifiedAssetMetadata;
    });

    collectionContext.updateAssetMetadata(section, changes);
  };

  const handleDragStart = (event, ui) => {
    gridStackRef.current?.float(false); // make the float false
    setDragging(true);
    draggingAssetRef.current = ui;
    gridStackRef.current?.setAnimation(true);
  };

  const handleDragStop = () => {
    gridStackRef.current?.setAnimation(false);
    if (sectionOverRef.current >= 0 && draggingAssetRef.current) {
      const assetId = draggingAssetRef.current.getAttribute("gs-id");

      if (assetId) {
        const asset = _.find(assets, { id: assetId });
        if (asset) {
          onMoveToSection(sectionOverRef.current, asset);
          handleAssetRemove(asset);
        }
      }
    }

    resetDragState();
    gridStackRef.current?.float(true); // make the float true
  };

  const resetDragState = () => {
    setDragging(false);
    sectionOverRef.current = -1;
    if (draggingAssetRef.current) {
      draggingAssetRef.current.style.visibility = "visible";
      draggingAssetRef.current = null;
    }

    document.querySelectorAll("[data-section-index]").forEach((item) => {
      item.classList.remove(sectionStyles.dragOver);
    });
  };

  const handleAssetRemove = (asset) => {
    const gridNodes = _.get(gridStackRef, "current.engine.nodes");
    const gridItem = _.find(gridNodes, { id: asset.id });
    if (gridStackRef.current && gridItem) {
      gridStackRef.current.setAnimation(true);
      if (gridItem?.el) {
        gridStackRef.current.removeWidget(gridItem.el, false);
      }
      gridStackRef.current.compact("list");
    }

    if (onRemove) {
      onRemove(asset);
    }
  };

  const handleAssetDimensionsChange = (asset, width, height) => {
    gridStackRef.current?.setAnimation(true);
    const gridNodes = gridStackRef.current?.engine.nodes;
    const gridItem = _.find(gridNodes, { id: asset.id });

    if (gridItem && gridItem.el) {
      const x = gridItem.x;
      const y = gridItem.y;
      gridStackRef.current?.update(gridItem.el, {
        x: x,
        y: y,
        w: width,
        h: height,
      });
    }
  };

  const handleMouseMove = (event: MouseEvent) => {
    const element = document.elementFromPoint(
      event.clientX,
      event.clientY,
    ) as HTMLElement;
    if (!element || !draggingAssetRef.current) return;

    const section = element.closest(".collection_section");
    if (section && section.classList.contains("collection_section")) {
      if (draggingAssetRef.current) {
        draggingAssetRef.current.style.visibility = "hidden";
      }

      const sectionIndex = section.getAttribute("data-section-index");
      if (sectionIndex) {
        section.classList.add(sectionStyles.dragOver);
        sectionOverRef.current = parseInt(sectionIndex);
      }
    } else {
      if (draggingAssetRef.current) {
        draggingAssetRef.current.style.visibility = "visible";
      }

      document.querySelectorAll("[data-section-index]").forEach((item) => {
        item.classList.remove(sectionStyles.dragOver);
      });
    }
  };

  const resetLayout = () => {
    if (gridStackRef.current) {
      gridStackRef.current.setAnimation(true);

      if (_.size(assets) > 0) {
        assets.map((asset) => {
          const assetIndex = _.findIndex(assets, { id: asset.id });
          const dimension = calculateDimensionsForCard(
            gridStackRef.current,
            assetIndex + 1,
            true,
            _.get(asset, "type") === "CONTACT_CARD",
          );
          const gridNodes = gridStackRef.current?.engine.nodes;
          const gridItem = _.find(gridNodes, { id: asset.id });
          if (gridItem && gridItem.el) {
            const { x, y, width, height } = dimension;
            gridStackRef.current?.update(gridItem.el, {
              x: x,
              y: y,
              w: width,
              h: height,
            });
          }
        });
      }
    }
  };

  const handleColorChange = () => {
    const banner = collection.banner || {};

    collectionContext.updateCollectionProperty("banner", {
      ...banner,
      descriptionOverlay: descriptionColor,
    });
  };

  return (
    <BentoContext.Provider value={bentoContext}>
      <div className={styles.bento}>
        <div
          className={cx("grid-stack", styles.assets)}
          ref={handleInitialised}
        >
          <div
            ref={descriptionRef}
            className={cx("grid-stack-item", styles.descriptionWidget)}
            // eslint-disable-next-line react/no-unknown-property
            gs-id="collectionDescription"
            // eslint-disable-next-line react/no-unknown-property
            gs-w="3"
            // eslint-disable-next-line react/no-unknown-property
            gs-h="2"
            // eslint-disable-next-line react/no-unknown-property
            gs-x="0"
            // eslint-disable-next-line react/no-unknown-property
            gs-y="0"
            // eslint-disable-next-line react/no-unknown-property
            gs-no-move="true"
            // eslint-disable-next-line react/no-unknown-property
            gs-no-resize="true"
            // eslint-disable-next-line react/no-unknown-property
            gs-locked="true"
          >
            <div
              style={{
                background: `rgba(${descriptionColor.r}, ${descriptionColor.g}, ${descriptionColor.b}, ${descriptionColor.a})`,
              }}
              className={cx(
                "grid-stack-item-content",
                cx(styles.overviewContainer, {
                  [styles.displayCollectionDescription]:
                    isDescriptionWidgetPlaced,
                }),
              )}
            >
              <CustomerLogo
                className={styles.customerLogo}
                collection={collection}
                menuState={menuState}
                onMenuChange={onMenuChange}
              />

              <div ref={descriptionElementRef} className={styles.description}>
                <CollectionDescription
                  collection={collection}
                  menuState={menuState}
                  disabled={!visible}
                  maxHeight={
                    descriptionBounds ? `${descriptionBounds.height}px` : "none"
                  }
                  onMenuChange={onMenuChange}
                />
              </div>

              {editing && (
                <ColorPicker
                  color={descriptionColor}
                  onChange={(color: any) => {
                    setDescriptionColor(color.rgb);
                  }}
                  onClose={handleColorChange}
                >
                  <div className={styles.colorButton}>
                    <i className="paperflite-pen"></i>
                  </div>
                </ColorPicker>
              )}
            </div>
          </div>

          {(assets || []).map((asset) => {
            if (
              _.get(asset, "type", "") === "ASSET" ||
              _.get(asset, "asset.entityType", "") === "asset"
            ) {
              return (
                <AssetCard
                  key={asset.id}
                  asset={asset}
                  editable={editing}
                  onClick={onClick}
                  onRemove={handleAssetRemove}
                  onResize={handleAssetDimensionsChange}
                  collection={collection}
                  sectionIndex={section}
                />
              );
            } else if (_.get(asset, "type") === "CONTACT_CARD") {

            return (
              <ContactCard
                key={asset.id}
                asset={asset}
                editable={editing}
                onRemove={handleAssetRemove}
                initializeAnimation={() => gridStackRef.current?.setAnimation(true)}
              />
            )};
          })}

          {showPlaceholder && (
            <div className={styles.placeholders}>
              <PlaceholderAssetCard height="100%" />
              <PlaceholderAssetCard height="100%" />
              <PlaceholderAssetCard height="50%" />
            </div>
          )}

          {empty && (
            <EmptyAssets
              canEdit={canEdit || false}
              onMenuChange={onMenuChange}
            />
          )}
        </div>
      </div>
    </BentoContext.Provider>
  );
};

interface EmptyAssetsProps {
  canEdit: boolean;
  // eslint-disable-next-line no-unused-vars
  onMenuChange: (state: any) => void;
}

const EmptyAssets: React.FC<EmptyAssetsProps> = ({ canEdit, onMenuChange }) => {
  const handleAddAsset = () => {
    onMenuChange({
      primaryAction: "edit",
      secondaryAction: "add-asset",
    });
  };

  return (
    <div className={styles.empty}>
      <div className={cx(styles.card, styles.image)}>
        <img src="/images/collections/empty.svg" />
      </div>
      <div className={cx(styles.card, styles.message)}>
        <p>It’s so empty here, let’s make this collection interesting...</p>
        {canEdit && (
          <Button
            variant={"primary"}
            icon="paperflite-edit"
            text="Edit"
            onClick={handleAddAsset}
          />
        )}
      </div>
      <div className={cx(styles.card)}></div>
    </div>
  );
};

export { BentoLayout };
